import { gsap } from "gsap";
import "./style.css";

const CARD_WIDTH = 320;
const CARD_GAP = 32; // matches gap-8 (2rem = 32px at default font-size)
const SIDE_SCALE = 0.82;
const FAR_SCALE = 0.68;
const DURATION = 0.45;

function initCarousel(wrapper) {
	const viewport = wrapper.querySelector(".thg-carousel__viewport");
	const track = wrapper.querySelector(".thg-carousel__track");
	const cards = [...wrapper.querySelectorAll(".thg-carousel__card")];
	const dots = [...wrapper.querySelectorAll(".thg-carousel__dot")];
	const prevBtn = wrapper.querySelector(".thg-carousel__arrow--prev");
	const nextBtn = wrapper.querySelector(".thg-carousel__arrow--next");

	if (!track || cards.length === 0) return;

	let activeIndex = parseInt(wrapper.dataset.active, 10) || 0;

	function trackOffset(index) {
		const vw = viewport.offsetWidth;
		const cardWidth = cards[0]?.offsetWidth || CARD_WIDTH;
		const gap = parseFloat(window.getComputedStyle(track).columnGap) || CARD_GAP;
		return vw / 2 - cardWidth / 2 - index * (cardWidth + gap);
	}

	function navigateTo(index, animate = true) {
		activeIndex = Math.max(0, Math.min(index, cards.length - 1));
		wrapper.dataset.active = activeIndex;

		const fn = animate ? gsap.to.bind(gsap) : gsap.set.bind(gsap);

		fn(track, {
			x: trackOffset(activeIndex),
			duration: DURATION,
			ease: "power2.out",
		});

		cards.forEach((card, i) => {
			const diff = Math.abs(i - activeIndex);
			const isActive = diff === 0;
			const isAdjacent = diff === 1;

			fn(card, {
				scale: isActive ? 1 : isAdjacent ? SIDE_SCALE : FAR_SCALE,
				opacity: isActive ? 1 : isAdjacent ? 0.65 : 0,
				duration: DURATION,
				ease: "power2.out",
			});

			card.classList.toggle("thg-carousel__card--active", isActive);
			card.style.pointerEvents = diff > 1 ? "none" : "auto";
			card.setAttribute("aria-hidden", isActive ? "false" : "true");
		});

		dots.forEach((dot, i) => {
			dot.classList.toggle("thg-carousel__dot--active", i === activeIndex);
		});

		if (prevBtn) prevBtn.disabled = activeIndex === 0;
		if (nextBtn) nextBtn.disabled = activeIndex === cards.length - 1;
	}

	navigateTo(activeIndex, false);

	if (prevBtn) prevBtn.addEventListener("click", () => navigateTo(activeIndex - 1));
	if (nextBtn) nextBtn.addEventListener("click", () => navigateTo(activeIndex + 1));

	cards.forEach((card, i) => {
		card.addEventListener("click", () => {
			if (i !== activeIndex) navigateTo(i);
		});
	});

	dots.forEach((dot, i) => {
		dot.addEventListener("click", () => navigateTo(i));
	});

	// Touch swipe — horizontal swipes navigate, vertical scroll is unaffected
	let touchStartX = 0;
	let touchStartY = 0;

	viewport.addEventListener("touchstart", (e) => {
		touchStartX = e.touches[0].clientX;
		touchStartY = e.touches[0].clientY;
	}, { passive: true });

	viewport.addEventListener("touchend", (e) => {
		const dx = e.changedTouches[0].clientX - touchStartX;
		const dy = e.changedTouches[0].clientY - touchStartY;
		if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
			navigateTo(dx < 0 ? activeIndex + 1 : activeIndex - 1);
		}
	}, { passive: true });

	let resizeTimer;
	window.addEventListener("resize", () => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(() => navigateTo(activeIndex, false), 100);
	});
}

function initAll() {
	document.querySelectorAll("[data-thg-carousel]").forEach(initCarousel);
}

document.addEventListener("DOMContentLoaded", initAll);

if (typeof window.acf !== "undefined") {
	window.acf.addAction("render_block_preview", initAll);
}
